import { useCallback, useEffect, useRef, useState } from 'react';
import { HubConnection } from '@microsoft/signalr';
import useSnackbar from '@core/hooks/useSnackbar';
import {
  createStudyExportConnection,
  joinStudyExport,
  leaveStudyExport,
  onExportStatusChanged,
} from '@core/signalr/studyExportHubClient';
import { ExportStatusPayload, ExportStatusView } from '@core/signalr/types';
import { requestExport } from 'app/features/study/api/exportStudy';

export type Phase =
  | 'idle'
  | 'posting'
  | 'connecting'
  | 'joining'
  | 'waiting'
  | 'reconnecting'
  | 'completing';

export interface ExportProgress {
  percent: number;
  totalSops: number;
  completedSops: number;
}

export interface UseStudyExportDownloadResult {
  phase: Phase;
  progress: ExportProgress | null;
  isDownloading: boolean;
  start: () => Promise<void>;
}

const ERROR_TITLE = 'Error al descargar';
const ERROR_TITLE_START = 'Error al iniciar la descarga';
const ERROR_TITLE_DISCONNECT = 'Conexión interrumpida';

const MAX_RECONNECT_ATTEMPTS = 5;
const RECONNECT_DELAYS_MS = [0, 2000, 5000, 10000, 30000];

const useStudyExportDownload = (studyInstanceUid: string): UseStudyExportDownloadResult => {
  const showSnackbar = useSnackbar();
  const [isDownloading, setIsDownloading] = useState(false);

  const [phase, setPhase] = useState<Phase>('idle');
  const [progress, setProgress] = useState<ExportProgress | null>(null);

  const phaseRef = useRef<Phase>('idle');
  const progressRef = useRef<ExportProgress | null>(null);
  const connRef = useRef<HubConnection | null>(null);
  const unsubscribeRef = useRef<(() => void) | null>(null);
  const isMountedRef = useRef(true);
  const reconnectAttemptRef = useRef(0);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const completingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const safeSetIsDownloading = useCallback((value: boolean) => {
    if (isMountedRef.current) {
      setIsDownloading(value);
    }
  }, []);

  const setPhaseSafe = useCallback((next: Phase) => {
    phaseRef.current = next;
    if (isMountedRef.current) setPhase(next);
  }, []);

  const setProgressSafe = useCallback((next: ExportProgress | null) => {
    progressRef.current = next;
    if (isMountedRef.current) setProgress(next);
  }, []);

  const clearReconnectTimer = useCallback(() => {
    if (reconnectTimerRef.current !== null) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
  }, []);

  const clearCompletingTimer = useCallback(() => {
    if (completingTimerRef.current !== null) {
      clearTimeout(completingTimerRef.current);
      completingTimerRef.current = null;
    }
  }, []);

  const cleanup = useCallback(async () => {
    clearReconnectTimer();
    clearCompletingTimer();
    reconnectAttemptRef.current = 0;
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }
    const conn = connRef.current;
    connRef.current = null;
    setPhaseSafe('idle');
    setProgressSafe(null);
    if (conn) {
      await leaveStudyExport(conn, studyInstanceUid);
      try {
        await conn.stop();
      } catch {
        /* ignore */
      }
    }
  }, [clearCompletingTimer, clearReconnectTimer, setPhaseSafe, setProgressSafe, studyInstanceUid]);

  const finishWithSuccess = useCallback(
    async (downloadUrl: string | null) => {
      if (!downloadUrl) {
        const message = 'No se pudo obtener el enlace de descarga.';
        console.error(`[StudyExport] ${ERROR_TITLE}: ${message}`);
        showSnackbar({
          title: ERROR_TITLE,
          message,
          type: 'error',
        });
        await cleanup();
        safeSetIsDownloading(false);
        return;
      }

      const a = document.createElement('a');
      a.href = downloadUrl;
      a.rel = 'noopener noreferrer';
      a.download = '';
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      a.remove();

      const prev = progressRef.current;
      setProgressSafe({
        percent: 100,
        totalSops: prev?.totalSops ?? 0,
        completedSops: prev?.completedSops ?? 0,
      });
      setPhaseSafe('completing');

      completingTimerRef.current = setTimeout(() => {
        completingTimerRef.current = null;
        void cleanup();
        safeSetIsDownloading(false);
      }, 2000);
    },
    [cleanup, safeSetIsDownloading, setPhaseSafe, setProgressSafe, showSnackbar],
  );

  const finishWithFailure = useCallback(
    async (title: string, message: string) => {
      console.error(`[StudyExport] ${title}: ${message}`);
      showSnackbar({ title, message, type: 'error' });
      await cleanup();
      safeSetIsDownloading(false);
    },
    [cleanup, safeSetIsDownloading, showSnackbar],
  );

  const handlePush = useCallback(
    (payload: ExportStatusPayload) => {
      if (phaseRef.current === 'completing' || phaseRef.current === 'idle') {
        return;
      }
      if (payload.status === ExportStatusView.Exporting) {
        setProgressSafe({
          percent: payload.percent,
          totalSops: payload.totalSops,
          completedSops: payload.completedSops,
        });
      } else if (payload.status === ExportStatusView.Complete) {
        void finishWithSuccess(payload.downloadUrl);
      } else if (payload.status === ExportStatusView.Failed) {
        void finishWithFailure(ERROR_TITLE, payload.failureReason ?? 'Error desconocido');
      } else {
        console.warn('[StudyExport] Unknown push payload', payload);
      }
    },
    [finishWithFailure, finishWithSuccess, setProgressSafe],
  );

  const establishRef = useRef<() => Promise<void>>(async () => {});
  const scheduleReconnectRef = useRef<(err?: Error) => void>(() => {});

  const establish = useCallback(async (): Promise<void> => {
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }
    const old = connRef.current;
    connRef.current = null;
    if (old) {
      try {
        await old.stop();
      } catch {
        /* ignore */
      }
    }

    setPhaseSafe('connecting');
    const conn = await createStudyExportConnection();

    conn.onclose((err) => {
      if (phaseRef.current === 'idle' || !isMountedRef.current) return;
      scheduleReconnectRef.current(err);
    });

    await conn.start();
    connRef.current = conn;
    unsubscribeRef.current = onExportStatusChanged(conn, handlePush);

    setPhaseSafe('joining');
    await joinStudyExport(conn, studyInstanceUid);

    setPhaseSafe('waiting');
    reconnectAttemptRef.current = 0;
  }, [handlePush, setPhaseSafe, studyInstanceUid]);

  const scheduleReconnect = useCallback(
    (err?: Error) => {
      if (phaseRef.current === 'idle' || !isMountedRef.current) return;

      if (reconnectAttemptRef.current >= MAX_RECONNECT_ATTEMPTS) {
        void finishWithFailure(
          ERROR_TITLE_DISCONNECT,
          err?.message ?? 'Se perdió la conexión durante la descarga. Intenta de nuevo.',
        );
        return;
      }

      const delay =
        RECONNECT_DELAYS_MS[reconnectAttemptRef.current] ??
        RECONNECT_DELAYS_MS[RECONNECT_DELAYS_MS.length - 1];
      reconnectAttemptRef.current += 1;
      setPhaseSafe('reconnecting');

      reconnectTimerRef.current = setTimeout(() => {
        reconnectTimerRef.current = null;
        if (phaseRef.current === 'idle' || !isMountedRef.current) return;
        establishRef.current().catch((e: any) => scheduleReconnectRef.current(e));
      }, delay);
    },
    [finishWithFailure, setPhaseSafe],
  );

  establishRef.current = establish;
  scheduleReconnectRef.current = scheduleReconnect;

  const start = useCallback(async () => {
    if (phaseRef.current !== 'idle') return;

    setPhaseSafe('posting');
    safeSetIsDownloading(true);

    let response;
    try {
      response = await requestExport(studyInstanceUid);
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ?? err?.message ?? 'Intenta de nuevo.';
      await finishWithFailure(ERROR_TITLE, msg);
      return;
    }

    if (!response || !response.status) {
      await finishWithFailure(ERROR_TITLE, 'Respuesta inválida del servidor.');
      return;
    }

    if (response.status === ExportStatusView.Complete) {
      await finishWithSuccess(response.downloadUrl);
      return;
    }

    if (response.status !== ExportStatusView.Exporting) {
      await finishWithFailure(
        ERROR_TITLE,
        response.failureReason ?? 'Estado de exportación inesperado.',
      );
      return;
    }

    setProgressSafe({ percent: 0, totalSops: 0, completedSops: 0 });

    try {
      await establish();
    } catch (err: any) {
      await finishWithFailure(ERROR_TITLE_START, err?.message ?? 'No se pudo conectar.');
    }
  }, [
    establish,
    finishWithFailure,
    finishWithSuccess,
    safeSetIsDownloading,
    setPhaseSafe,
    setProgressSafe,
    studyInstanceUid,
  ]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (reconnectTimerRef.current !== null) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      if (completingTimerRef.current !== null) {
        clearTimeout(completingTimerRef.current);
        completingTimerRef.current = null;
      }
      if (connRef.current) {
        const conn = connRef.current;
        connRef.current = null;
        void leaveStudyExport(conn, studyInstanceUid);
        void conn.stop().catch(() => {});
      }
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
      phaseRef.current = 'idle';
    };
  }, [studyInstanceUid]);

  return { isDownloading, start, phase, progress };
};

export { useStudyExportDownload };
export default useStudyExportDownload;
